import type { BimContractResultData, BimContractResultsViewData, BimNormalContractResultData } from 'gql/graphql';
import type { IBimContractResultPlanDataItem } from 'pages/Project/BimProject/WorkOrder/types';

import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { mapResultPlan } from '../../hooks/useBimContractFormData';

interface IUseAccumulationSheetData {
    resultPlan?: (IBimContractResultPlanDataItem | null)[] | null;
    frameContractResults?: BimContractResultsViewData | null;
    normalContractResults?: (BimNormalContractResultData | null)[] | undefined | null;
}

export const useAccumulationSheetData = (params: IUseAccumulationSheetData) => {
    const { resultPlan, frameContractResults, normalContractResults } = params;

    return useMemo(() => {
        if (!frameContractResults && !normalContractResults) {
            return resultPlan;
        }

        if (normalContractResults) {
            return mapResultPlan(normalContractResults)?.map((item) => {
                const constructionTypeCompletedResultMapped =
                    (item as unknown as BimNormalContractResultData)?.constructionTypeCompletedResult?.reduce(
                        (acc, result) => {
                            acc[`completedResultPrice${result.constructionTypeId}`] = result.completedPrice;
                            acc[`completedResultDeviation${result.constructionTypeId}`] = result.deviation;

                            return acc;
                        },
                        {} as Record<string, any>,
                    ) || {};

                return {
                    ...item,
                    ...constructionTypeCompletedResultMapped,
                };
            });
        }

        return (
            mapResultPlan(frameContractResults?.contractResults)?.map((item) => {
                const workOrderFields = frameContractResults?.workOrders?.reduce((acc, workOrder) => {
                    const { planPrice, completedPrice, deviation, constructionTypeResults } =
                        (item as unknown as BimContractResultData)?.workOrderResults?.find(
                            (r) => r.workOrderId === workOrder.id,
                        ) || {};

                    const constructionTypeResultsMapped =
                        constructionTypeResults?.reduce?.(
                            (a, result) => {
                                a[`planPriceWorkOrder${workOrder.id}_${result.constructionTypeId}`] = result.planPrice;
                                a[`completedPriceWorkOrder${workOrder.id}_${result.constructionTypeId}`] =
                                    result.completedPrice;

                                return a;
                            },
                            {} as Record<string, any>,
                        ) || {};

                    acc = {
                        ...acc,
                        ...constructionTypeResultsMapped,
                        [`planPriceWorkOrder${workOrder.id}`]: planPrice,
                        [`completedPriceWorkOrder${workOrder.id}`]: completedPrice,
                        [`deviationWorkOrder${workOrder.id}`]: deviation,
                    };

                    return acc;
                }, {});

                return {
                    ...item,
                    ...workOrderFields,
                    id: `${item?.name}_${uuidv4()}`,
                };
            }) || []
        );
    }, [frameContractResults, normalContractResults, resultPlan]);
};
